import React, {useCallback, useContext, useEffect, useState, useMemo} from 'react';
import dayjs from 'dayjs';
import { css } from "styled-components/macro"; //eslint-disable-line

import {DataGrid, GridCellModes} from '@mui/x-data-grid';
import {Alert, Button, Box, Chip, Container, Card, CardContent, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, IconButton, InputAdornment,
  MenuItem, Stack, TextField, Typography} from '@mui/material';
import {AccountBalance, AttachFile, Delete, Payment, Receipt, Schedule, Savings} from '@mui/icons-material';

import AnimationRevealPage from "components/AnimationRevealPage.js";
import Header from "components/Header.js";
import {createExpense, fetchExpenses, fetchBalanceSummary, updateExpense,
  linkDocumentToExpense, unlinkDocumentFromExpense} from "helpers/finance.js";
import {createAndUploadDocument, downloadDocument, deleteDocument} from "helpers/documents.js";
import AuthContext from 'contexts/AuthContext';

const DEFAULT_OFFSET = 0;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_OPTIONS = [5, 10, 25, 50, 100];
const CATEGORY_OPTIONS = ["credit", "debit"];

export default function Finance() {
  const { role, designation, user } = useContext(AuthContext);
  const inputFile = React.useRef(null);
  const billInputFile = React.useRef(null);

  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [cellModesModel, setCellModesModel] = useState({});
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [error, setError] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [billTargetExpense, setBillTargetExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pgModel, setPgnModel] = useState({page: DEFAULT_OFFSET,
    pageSize: DEFAULT_PAGE_SIZE});

  // A Secretary-designated EXECUTIVE has handed Finance off to a Treasurer -
  // an undesignated or President-designated EXECUTIVE keeps full access.
  const hasPermission = useMemo(() =>
    role === "ADMIN" || (role === "EXECUTIVE" && designation !== "SECRETARY"), [role, designation]);
  const hasChanges = useMemo(() => {
    return expenses.some(exp => exp.isEdited && !exp.isNew)
  }, [expenses]);

  // Finance handlers
  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable || (event.target?.nodeType === 1 && !event.currentTarget.contains(event.target))) {
      return;
    }
    setCellModesModel((prevModel) => {
      const newModel = Object.keys(prevModel).reduce((acc, id) => ({
        ...acc,
        [id]: Object.keys(prevModel[id]).reduce((acc2, field) => ({
          ...acc2,
          [field]: { mode: GridCellModes.View }
        }), {})
      }), {});
      return {
        ...newModel,
        [params.id]: {
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit }
        }
      };
    });
  }, []);
  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);
  const handleRowUpdate = useCallback((updatedRow, originalRow) => {
    setExpenses(prev => prev.map(exp =>
      exp.id === updatedRow.id
        ? { ...updatedRow, isEdited: true, updated_at: new Date(), updated_by: user.name }
        : exp
    ));
    return updatedRow;
  }, [user.name]);
  const getNewId = useCallback((exps) => {
    if (!exps || exps.length === 0) return 1;
    return Math.max(...exps.map(exp => exp.id)) + 1;
  }, []);
  const handleAddEntry = useCallback(() => {
    const newExpense = {
      id: getNewId(expenses), title: "", description: "",
      amount: 0, category: "debit", date: new Date(), document_id: null,
      created_by: user.name, created_at: new Date(), updated_by: user.name,
      updated_at: new Date(), isNew: true};
    setEditingExpense(newExpense);
    setSelectedFile(null);
    setExpenseDialogOpen(true);
  }, [expenses, getNewId, user.name]);
  const loadExpenses = useCallback(async () => {
    setBalanceLoading(true);
    try {
      await fetchBalanceSummary(setBalanceSummary);
    } catch (err) {
      console.error('Error loading balance summary:', err);
    } finally {
      setBalanceLoading(false);
    }
    setLoading(true);
    setError(null);
    try {
      await fetchExpenses(pgModel.page + 1, pgModel.pageSize,
        (list) => setExpenses((list || []).map(exp => ({...exp, date: exp.date ? new Date(exp.date) : new Date()}))),
        setRowCount);
    } catch (err) {
      console.error('Error loading expenses:', err);
      setError('Failed to load expenses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pgModel]);
  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Only handle edited existing expenses (not new ones)
      const editedExpenses = expenses.filter(exp =>
        exp.isEdited && !exp.isNew && exp.amount > 0 && exp.category !== ""
      );
      const promises = editedExpenses.map(exp => {
        const request = {
          title: exp.title,
          description: exp.description,
          amount: exp.amount,
          category: exp.category.toLowerCase(),
          date: dayjs(exp.date).toISOString()
        };
        return updateExpense(exp.id, request);
      });

      await Promise.all(promises);
      await loadExpenses();
    } catch (err) {
      console.error('Error saving expenses:', err);
      setError('Failed to save expenses. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [expenses, loadExpenses]);
  const handleCancel = useCallback(() => {
    loadExpenses();
  }, [loadExpenses]);
  // Documents handlers
  const handleFileSelected = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
    event.target.value = '';
  }, []);
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
  }, []);
  const handleDownloadBill = useCallback(async (expense) => {
    if (!expense.document_id) return;
    try {
      await downloadDocument(expense.document_id);
    } catch (err) {
      console.error('Error downloading bill:', err);
      setError('Failed to download bill. Please try again.');
    }
  }, []);
  const handleAttachBillClick = useCallback((expense) => {
    setBillTargetExpense(expense);
    billInputFile.current.click();
  }, []);

  const handleBillFileSelected = useCallback(async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file || !billTargetExpense) return;

    const expense = billTargetExpense;
    setBillTargetExpense(null);
    setError(null);
    try {
      const createDocRequest = {
        filename: file.name,
        title: `${expense.title} - Bill`,
        description: `Bill for expense: ${expense.title}`,
        category: 'bills',
        file_size: file.size,
        mime_type: file.type
      };
      const createdDoc = await createAndUploadDocument(createDocRequest, file);
      await linkDocumentToExpense(expense.id, createdDoc.id);
      if (expense.document_id) {
        await deleteDocument(expense.document_id).catch(err =>
          console.error('Failed to clean up replaced bill:', err));
      }
      await loadExpenses();
    } catch (err) {
      console.error('Error attaching bill:', err);
      setError('Failed to attach bill. Please try again.');
    }
  }, [billTargetExpense, loadExpenses]);

  const handleRemoveBill = useCallback(async (expense) => {
    if (!expense.document_id) return;
    if (!window.confirm('Remove the attached bill from this expense?')) return;
    setError(null);
    try {
      await unlinkDocumentFromExpense(expense.id);
      await deleteDocument(expense.document_id).catch(err =>
        console.error('Failed to delete removed bill:', err));
      await loadExpenses();
    } catch (err) {
      console.error('Error removing bill:', err);
      setError('Failed to remove bill. Please try again.');
    }
  }, [loadExpenses]);

  const handleDialogSubmit = useCallback(async () => {
    if (!editingExpense?.title || !editingExpense?.category || editingExpense?.amount <= 0) {
      setError('Title, category, and valid amount are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let documentId = editingExpense.document_id;

      // If there's a selected file, upload it first
      if (selectedFile) {
        const createDocRequest = {
          filename: selectedFile.name,
          title: `${editingExpense.title} - Bill`,
          description: `Bill for expense: ${editingExpense.title}`,
          category: 'bills',
          file_size: selectedFile.size,
          mime_type: selectedFile.type
        };

        const createdDoc = await createAndUploadDocument(createDocRequest, selectedFile);
        documentId = createdDoc.id;
      }

      // Create the expense with document_id
      const expenseRequest = {
        title: editingExpense.title,
        description: editingExpense.description,
        amount: editingExpense.amount,
        category: editingExpense.category.toLowerCase(),
        date: dayjs(editingExpense.date).toISOString(),
        document_id: documentId
      };

      await createExpense(expenseRequest);

      setExpenseDialogOpen(false);
      setEditingExpense(null);
      setSelectedFile(null);
      await loadExpenses();
    } catch (err) {
      console.error('Error saving expense:', err);
      setError('Failed to save expense. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editingExpense, selectedFile, loadExpenses]);
  const handleDialogCancel = useCallback(() => {
    setExpenseDialogOpen(false);
    setEditingExpense(null);
    setSelectedFile(null);
  }, []);

  const columns = useMemo(() => [
    { field: 'date', headerName: 'Date', width: 150, editable: true, type: 'date' },
    { field: 'title', headerName: 'Title', minWidth: 100, flex: 1, editable: true },
    { field: 'category', headerName: 'Category', width: 100, type: "singleSelect",
      valueOptions: CATEGORY_OPTIONS, editable: true },
    { field: 'amount', headerName: 'Amount', width: 150, editable: true, type: 'number' },
    { field: 'description', headerName: 'Description', minWidth: 100, flex: 1, editable: true },
    { field: 'document_id', headerName: 'Bill', width: 140,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          {params.row.document_id && (
            <IconButton size="small" color="primary" onClick={() => handleDownloadBill(params.row)}>
              <Receipt fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={() => handleAttachBillClick(params.row)}
            title={params.row.document_id ? 'Replace bill' : 'Attach bill'}>
            <AttachFile fontSize="small" />
          </IconButton>
          {params.row.document_id && (
            <IconButton size="small" color="error" onClick={() => handleRemoveBill(params.row)}
              title="Remove bill">
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )
    },
    { field: 'created_by', headerName: 'Added By', minWidth: 80, flex: 1 },
    { field: 'created_at', headerName: 'Added At', width: 150, type: 'date',
      valueGetter: (params) => params.row?.created_at ? new Date(params.row.created_at) : new Date() },
    { field: 'updated_by', headerName: 'Updated By', minWidth: 80, flex: 1 },
    { field: 'updated_at', headerName: 'Updated At', width: 150, type: 'date',
      valueGetter: (params) => params.row?.updated_at ? new Date(params.row.updated_at) : new Date() },
  ], [handleDownloadBill, handleAttachBillClick, handleRemoveBill]);

  useEffect(() => {
    if (hasPermission) {
      loadExpenses();
    }
  }, [loadExpenses, hasPermission]);

  if (!hasPermission) {
    return (
      <AnimationRevealPage>
        <Header />
        <Container>
          <Alert severity="error">Permission denied</Alert>
        </Container>
      </AnimationRevealPage>
    );
  }

  return (
    <AnimationRevealPage>
      <Header />
      <Container style={{padding: 15, marginTop: 40, maxWidth: "100%"}}>
        {error && (
          <Alert severity="error" style={{marginBottom: 20}}>
            {error}
          </Alert>
        )}
        <input type="file" ref={inputFile} accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
          onChange={handleFileSelected} style={{display: 'none'}} />
        <input type="file" ref={billInputFile} accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
          onChange={handleBillFileSelected} style={{display: 'none'}} />
        {/* Summary Cards */}
        {!balanceLoading && balanceSummary && (
          <Grid container spacing={2} style={{ marginBottom: 50 }}>
            <div style={{width: 220}}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Current Balance
                      </Typography>
                      <Typography
                        variant="h5"
                        color={balanceSummary.current_balance >= 0 ? "success.main" : "error.main"}
                      >
                        ₹{balanceSummary.current_balance?.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '0.00'}
                      </Typography>
                    </Box>
                    <AccountBalance
                      color={balanceSummary.current_balance >= 0 ? "success" : "error"}
                      fontSize="large"
                    />
                  </Box>
                </CardContent>
              </Card>
            </div>
            <div style={{width: 220}}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Income
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        ₹{balanceSummary.total_income?.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '0.00'}
                      </Typography>
                    </Box>
                    <Savings color="success" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </div>
            <div style={{width: 220}}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Expenses
                      </Typography>
                      <Typography variant="h5" color="error.main">
                        ₹{balanceSummary.total_expenses?.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '0.00'}
                      </Typography>
                    </Box>
                    <Payment color="error" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </div>
            <div style={{width: 300}}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Last Updated
                      </Typography>
                      <div style={{display: 'flex', flexDirection: 'row'}}>
                        <Typography variant="h6" style={{marginRight: 10, fontWeight: 100}}>
                          {dayjs(balanceSummary.last_updated).format('MMM DD, YYYY')}
                        </Typography>
                        <Chip size="small" color="primary" variant="outlined"
                          label={dayjs(balanceSummary.last_updated).format('h:mm A')} />
                      </div>
                    </Box>
                    <Schedule color="primary" fontSize="large" />
                  </Box>
                </CardContent>
              </Card>
            </div>
          </Grid>
        )}
        {/* Add/Edit Expense Dialog */}
        <Dialog open={expenseDialogOpen} onClose={handleDialogCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Expense</DialogTitle>
          <DialogContent>
            <TextField margin="dense" label="Date" type="date" fullWidth variant="outlined"
              value={editingExpense?.date ? dayjs(editingExpense.date).format('YYYY-MM-DD') : ''}
              onChange={(e) => setEditingExpense(prev => ({...prev, date: new Date(e.target.value)}))}
              required InputLabelProps={{ shrink: true }}
            />
            <TextField margin="dense" label="Title" variant="outlined"
              value={editingExpense?.title || ''} required fullWidth
              onChange={(e) => setEditingExpense(prev => ({...prev, title: e.target.value}))}
            />
            <TextField select margin="dense" label="Category" fullWidth required
              variant="outlined" value={editingExpense?.category || 'debit'}
              onChange={(e) => setEditingExpense(prev => ({...prev, category: e.target.value}))} >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </TextField>
            <TextField margin="dense" label="Amount" type="number" fullWidth
              variant="outlined" value={editingExpense?.amount}
              onChange={(e) => setEditingExpense(prev => ({...prev, amount: parseFloat(e.target.value) || 0}))}
              required InputProps={{startAdornment: <InputAdornment position="start">₹</InputAdornment>}}
            />
            <TextField margin="dense" label="Description" fullWidth variant="outlined" multiline
              rows={3} value={editingExpense?.description || ''}
              onChange={(e) => setEditingExpense(prev => ({...prev, description: e.target.value}))}
            />
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Attach Bill (Optional)
              </Typography>
              {!selectedFile ? (
                <Button variant="outlined" startIcon={<AttachFile />}
                  onClick={() => inputFile.current.click()} fullWidth >
                  Upload Bill
                </Button>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="space-between"
                  p={2} border={1} borderColor="primary.main" borderRadius={1} >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Receipt color="primary" />
                    <Box>
                      <Typography variant="body2">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" color="error" onClick={handleRemoveFile} >
                    <Delete />
                  </IconButton>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleDialogSubmit} variant="contained" disabled={saving} >
              {editingExpense?.category === "debit" ? 'Create Expense' : 'Create Income'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Expences Table */}
        <DataGrid autoHeight loading={loading} style={{marginBottom: 20}}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#575757", whiteSpace: "normal",
              lineHeight: "normal", fontWeight: 600,
            },
            "& .MuiDataGrid-columnHeaders": {
              maxHeight: "168px !important"
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none"
            }
          }}
          initialState={{
            sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
            columns: {columnVisibilityModel: {
              "created_by": false, "created_at": false,
              "updated_by": false, "updated_at": false
            }},
          }}
          rows={expenses}
          columns={columns}
          cellModesModel={cellModesModel}
          pageSizeOptions={PAGE_OPTIONS}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={pgModel}
          onPaginationModelChange={setPgnModel}
          onCellModesModelChange={handleCellModesModelChange}
          onCellClick={handleCellClick}
          disableRowSelectionOnClick
          processRowUpdate={handleRowUpdate}
        />
        <Stack direction="row" spacing={1}>
          <Button color="primary" variant="contained"
            onClick={handleAddEntry} disabled={saving} >+ Add Entry</Button>
          <Button
            color="success"
            style={{ marginLeft: 'auto' }}
            variant="outlined"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            color="success"
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Container>
    </AnimationRevealPage>
  );
}
